// src/rms/pages/VerticalPicker.js
// ============================================================================
// Vertical picker + auto-provisioning
// ============================================================================
// The whole configuration step is this one screen. Choosing a card decides the
// asset fields, the vocabulary, the required workflow steps, the default rate
// cadence and the ad category — all at once, from the preset.
//
// The card preview is generated from the preset itself, so what the operator is
// promised here is literally what gets provisioned.
// ============================================================================

import React, { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { VERTICAL_PRESETS } from "../verticals/presets";
import { useRms } from "../context/RmsContext";
import { Field, TextInput, Select, Banner } from "../components/ui";
import { SUPPORTED_LOCALES, getLocale } from "../i18n/locale";
import {
  CURRENCIES,
  countryOptions,
  currencyForCountry,
  localeForCountry,
} from "../i18n/countries";
import Brandmark from "../components/Brandmark";

function VerticalPicker() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { provision, t, locale, changeLocale } = useRms();

  /**
   * A trade named in the URL arrives already chosen.
   *
   * The landing page's twelve chips link here with ?vertical=, and without
   * this they all landed on the same grid of twelve -- asking the visitor to
   * find again the thing they had just clicked. Validated against the presets
   * rather than trusted, so a hand-edited URL selects nothing instead of
   * putting an unknown key into state.
   */
  const [selected, setSelected] = useState(() => {
    const asked = params.get("vertical");
    return VERTICAL_PRESETS.some((v) => v.key === asked) ? asked : null;
  });
  const [businessName, setBusinessName] = useState("");
  const [country, setCountry] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const preset = VERTICAL_PRESETS.find((v) => v.key === selected) || null;

  /**
   * The trade's own name and pitch, translated.
   *
   * RmsContext exposes verticalLabel for this, but only once an operator is
   * loaded -- and on this screen there is not one yet, which is the entire
   * point of the screen. So the same catalogue keys are read directly. Without
   * this, the twelve cards a French visitor is choosing between were the only
   * English left on an otherwise translated page.
   */
  const labelFor = (v) => t(`vertical.${v.key}.label`, v.label);
  const taglineFor = (v) => t(`vertical.${v.key}.tagline`, v.tagline);


  // Country names come from Intl, so they arrive already translated and sorted
  // the way the current language sorts them.
  const countries = useMemo(() => countryOptions(locale || getLocale()), [locale]);

  /**
   * Choosing a country fills in the two things it reliably implies -- currency
   * and language -- and nothing else. Tax is not asked here at all: it is set
   * up from the dashboard, where there is room to get it right, and a rate we
   * guessed would become the operator's liability anyway.
   */
  const handleCountry = (code) => {
    setCountry(code);
    if (!code) return;
    setCurrency(currencyForCountry(code));
    const suggested = localeForCountry(code);
    if (suggested) changeLocale(suggested);
  };

  const handleStart = async () => {
    if (!preset) return;

    setBusy(true);
    setError(null);

    const result = await provision({
      verticalKey: preset.key,
      businessName:
        businessName.trim() ||
        t("verticalPicker.defaultName", "My {trade}", { trade: labelFor(preset) }),
      country: country || null,
      currency,
      defaultLocale: locale,
    });

    setBusy(false);
    if (!result.success) {
      setError(result.error);
      return;
    }
    navigate("/rms");
  };

  return (
    <div className="rms-root" style={{ minHeight: "100vh" }}>
      <div style={{ maxWidth: 1080, margin: "0 auto", padding: "32px 24px 64px" }}>
        {/* Larger than the landing header's, not the same: there the lockup is
            balanced against a row of 38px controls, and here it stands alone
            above a 26px heading with nothing to hold its own against. */}
        <Brandmark size={34} className="rms-picker-brand" style={{ padding: "0 0 24px" }} />

        <h1 className="rms-page-title" style={{ fontSize: 26 }}>
          {t("verticalPicker.whatDoYouRent", "What do you rent out?")}
        </h1>
        <p className="rms-page-sub" style={{ marginBottom: 24, maxWidth: 620 }}>
          {t("verticalPicker.weLlSetUp", "We'll set up your fields, terminology, checkout steps and rates to match. You can adjust anything afterwards.")}
        </p>

        {error ? <Banner tone="danger">{error}</Banner> : null}

        <div className="rms-picker-grid">
          {/* Name and tagline, and nothing else.
              The cards used to carry up to five pills. "12 fields" was an
              internal spec on a signup screen -- no better-or-worse direction
              to compare on, and a number beside the word "fields" reads as
              work to do. "billed by the day" was on eight of the twelve, so it
              distinguished nothing, and the taglines say the cadence anyway:
              storage stated "billed monthly" in its tagline and then twice
              more in pills. The trade's own name and one line about it are
              what somebody actually chooses on. */}
          {VERTICAL_PRESETS.map((v) => {
            const isSelected = selected === v.key;
            return (
              <button
                key={v.key}
                type="button"
                className={`rms-picker-card${isSelected ? " is-selected" : ""}`}
                onClick={() => setSelected(v.key)}
                aria-pressed={isSelected}
              >
                <span className="rms-picker-name">{labelFor(v)}</span>
                <span className="rms-picker-tag">{taglineFor(v)}</span>
              </button>
            );
          })}
        </div>

        {preset ? (
          <div className="rms-card rms-card-pad" style={{ marginTop: 26 }}>
            <h2 className="rms-section-title">
              {t("verticalPicker.setUpYour", "Set up your {trade} business", {
                trade: labelFor(preset),
              })}
            </h2>

            <div className="rms-field-grid">
              <Field label={t("start.businessName", "Business name")}>
                <TextInput
                  value={businessName}
                  onChange={setBusinessName}
                  placeholder={t("verticalPicker.defaultName", "My {trade}", { trade: labelFor(preset) })}
                />
              </Field>
              <Field
                label={t("start.country", "Country")}
                help={t("start.countryHelp", "Sets your currency and language. You can change both.")}
              >
                <Select
                  value={country}
                  onChange={handleCountry}
                  options={countries}
                  placeholder={t("start.chooseCountry", "Choose a country")}
                />
              </Field>
              <Field label={t("start.currency", "Currency")}>
                <Select
                  value={currency}
                  onChange={setCurrency}
                  options={CURRENCIES}
                  placeholder={t("start.selectCurrency", "Select currency")}
                />
              </Field>
              <Field label={t("start.language", "Language")}>
                <Select
                  value={locale}
                  onChange={changeLocale}
                  options={SUPPORTED_LOCALES.map((l) => ({ value: l.code, label: l.endonym }))}
                />
              </Field>
            </div>



            <div style={{ display: "flex", gap: 10, marginTop: 18, flexWrap: "wrap" }}>
              <button
                type="button"
                className="rms-btn rms-btn--primary"
                onClick={handleStart}
                disabled={busy}
              >
                {busy
                  ? t("verticalPicker.settingUp", "Setting up…")
                  : t("verticalPicker.createMy", "Create my business")}
              </button>
              <button type="button" className="rms-btn rms-btn--ghost" onClick={() => setSelected(null)} disabled={busy}>
                {t("verticalPicker.chooseADifferentType", "Choose a different type")}
              </button>
            </div>
            <p className="rms-help" style={{ marginTop: 10 }}>
              {t(
                "verticalPicker.sampleNote",
                "We'll add a little sample data so you can see how it works. Clear it any time from Settings."
              )}
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
}



export default VerticalPicker;
